import { zodResolver } from '@hookform/resolvers/zod';
import FormSection from 'components/FormSection';
import { Modal } from 'components/Modal/Modal';
import { WizardStateBlock } from 'components/StepperWizard/WizardStateBlock';
import { GraniteLabel } from 'components/V2/Label/GraniteLabel';
import { useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { RebillServiceDetails, RebillServiceDetailsSchema } from './schemas';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import PhoneNumberInput from 'components/V2/Input/PhoneNumber';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { GraniteSelect, OptionType } from 'components/Select/Select';
import { SearchAccessAddressBar } from 'screens/AccessExpressV3/BaseComponents/Cart/Locations/SearchAccessAddressBar';
import { QuoteLocation } from 'api/accessexpress/schema';
import Divider from 'components/Divider/Divider';
import { useMutation, useQuery } from 'react-query';
import ServiceAddressFormSection from 'screens/AccessExpressV3/AccessCheckout/ServiceAddressFormSection';
import { Add, InformationCircle, Trash } from 'react-ionicons';
import { getAccessParentAccounts } from 'api/accessexpressv3/api';
import {
  billinTypeOptions,
  invoiceDeliveryOptions,
} from 'shared/constants/options';
import { getInvoiceStyleOptions } from 'screens/LetUsHelp/GetStarted/CreateChildAccount/utils';
import { postInvoiceRebill } from 'api/orders/api';
import React from 'react';
import showToast from 'components/Toast/Toast';
import Loader from 'components/Loader';

type UnitOption = { label: 'Mb/s' | 'Gb/s'; value: 'Mbps' | 'Gbps' };

export const INVOICE_SERVICE_TYPES = [
  'Broadband',
  'DIA',
  'PIP/GSE',
  'Mobility',
  'Hosted PBX/EPOTS',
  'DID',
  'POTS',
  'RCF',
  'SIP',
  'Toll-Free',
  'LD',
  'Television',
];

export const INVOICE_SERVICE_SUBTYPES = [
  'ADSL',
  'Cable',
  'Dry Loop',
  'Fiber',
  'Satellite',
  'T1',
  'Wireless',
];

interface RebillServiceDetailsModalProps {
  isOpen: boolean;
  close: () => void;
  onCancel?: () => void;
  onConfirm?: () => void;
  invoiceId?: string;
}

const step1Fields: (keyof RebillServiceDetails)[] = [
  'site',
  'business_name',
  'unit',
  'name',
  'phone_number',
  'parent_account',
  'location_name',
  'billing_type',
  'invoice_delivery_method',
  'invoice_style',
];

const step2Fields: (keyof RebillServiceDetails)[] = [
  'vendor_name',
  'vendor_account_number',
  'mrc',
  'contract_start_date',
  'contract_end_date',
  'services',
];

const unitOptions = [
  { label: 'Mb/s', value: 'Mbps' },
  { label: 'Gb/s', value: 'Gbps' },
] as const satisfies readonly UnitOption[];

const RebillServiceDetailsModal = ({
  isOpen,
  close,
  onConfirm,
  invoiceId,
}: RebillServiceDetailsModalProps) => {
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
    trigger,
    register,
    resetField,
    reset,
  } = useForm<RebillServiceDetails>({
    resolver: zodResolver(RebillServiceDetailsSchema),
    defaultValues: {
      services: [
        {
          service_type: { label: '', value: '' },
          bandwidth: { amount: '', unit: { value: 'Mbps', label: 'Mb/s' } },
        },
      ],
    },
  });

  const [step, setStep] = useState(1);
  const [addressValue, setAddressValue] = useState<
    QuoteLocation & { street?: string | null | undefined }
  >();
  const [parentAccountOptions, setParentAccountOptions] = useState<
    { label: string; value: string; subvalue?: string }[]
  >([]);

  const [wtnCounts, setWtnCounts] = useState<{ [key: number]: number }>({});

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'services',
  });

  const { mutate, isLoading } = useMutation(
    (data: RebillServiceDetails) => {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      return postInvoiceRebill(invoiceId, data);
    },
    {
      onSuccess: () => {
        close();
        reset({});
        onConfirm?.();
        showToast.confirmation({ message: 'Rebill process was initiated' });
      },
    },
  );

  const onSubmit = (data: RebillServiceDetails) => {
    const formattedData = {
      ...data,
      contract_start_date: data.contract_start_date || undefined,
      contract_end_date: data.contract_end_date || undefined,
    };
    mutate(formattedData);
  };

  const onNext = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    const fieldsToValidate = step === 1 ? step1Fields : step2Fields;
    const valid = await trigger(fieldsToValidate);
    if (!valid) return;
    setStep((prev) => prev + 1);
  };

  const onBack = () => {
    if (step > 1) setStep((prev) => prev - 1);
    else close();
  };

  const [location, currentMacnum, currentBillingType, currentCity] = watch([
    'site',
    'site.macnum',
    'billing_type',
    'site.city',
  ]);

  const shouldGetParentAccounts = useMemo(
    () => !currentMacnum && !!currentCity,
    [currentCity, currentMacnum],
  );

  useQuery(['access-parent-accounts'], () => getAccessParentAccounts(), {
    enabled: shouldGetParentAccounts,
    staleTime: Infinity,
    onSuccess: (data) => {
      setParentAccountOptions(
        data.map((item) => {
          const parts = item.name
            ? item.name.split('-').map((part) => part.trim())
            : ['', ''];
          return { label: parts[0], value: parts[0], subvalue: parts[1] };
        }),
      );
    },
  });

  const renderBandwidth = (index: number) => (
    <div className="flex flex-row items-center gap-0">
      <GraniteInput
        {...register(`services.${index}.bandwidth.amount`)}
        label="Bandwidth"
        error={
          errors.services?.[index]?.bandwidth?.amount?.message ||
          errors.services?.[index]?.bandwidth?.unit?.message
        }
        innerInputClassName="!w-full !box-border !rounded-r-none"
      />
      <Controller
        name={`services.${index}.bandwidth.unit`}
        control={control}
        render={({ field }) => (
          <GraniteSelect<UnitOption>
            {...field}
            options={unitOptions}
            label="unit"
            value={field.value}
            onChange={field.onChange}
            className="basis-1/2 self-baseline [&>label]:invisible"
            classNames={{ control: () => '!rounded-l-none' }}
          />
        )}
      />
    </div>
  );

  const addService = () => {
    if (fields.length < 5) {
      append({
        service_type: { label: '', value: '' },
        service_identifier: '',
        wtns: [],
      });
      setWtnCounts((prev) => ({
        ...prev,
        [fields.length]: 0,
      }));
    }
  };

  const removeService = (index: number) => {
    remove(index);
    setWtnCounts((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  return (
    <Modal
      isVisible={isOpen}
      close={close}
      className="!max-h-full !w-[1012px] justify-between sm:!max-h-[90vh]"
      scrollableClassName="min-h-full"
      enableScrolling
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between rounded-lg bg-background-base-surface-2"
      >
        <div className="flex flex-col gap-2 rounded-t-lg bg-background-base-surface-3 px-8 py-6 font-bold leading-9">
          <h1 className="text-xl text-content-base-default lg:text-2xl">
            Provide invoice details
          </h1>
          <div className="grid grid-cols-2 items-end gap-1">
            <WizardStateBlock
              label="Location info"
              isActive={step >= 1}
              isCompleted={step > 1}
              classNames="flex text-base"
            />
            <WizardStateBlock
              label="Invoice details"
              isActive={step >= 2}
              isCompleted={step > 2}
              classNames="flex text-base"
            />
          </div>
        </div>

        {/* Step 1 */}
        {step === 1 && (
          <div className="flex h-full w-full flex-col gap-8 p-8">
            {location && !currentMacnum && (
              <ServiceAddressFormSection
                location={{ ...location, id: location?.id?.toString() }}
                className="w-full"
              />
            )}
            <FormSection title="Service address" className="w-full">
              <GraniteLabel />
              <Controller
                name="site"
                control={control}
                render={({ field }) => (
                  <SearchAccessAddressBar
                    {...field}
                    value={addressValue}
                    placeholder="Search by address, site name, or child account"
                    onAddressSelected={(address) => {
                      //@ts-expect-error err

                      setValue(`site`, address);
                      setAddressValue(address);
                    }}
                    error={errors?.site?.message && 'An address is required'}
                    className="col-span-2"
                  />
                )}
              />
            </FormSection>
            {addressValue && !currentMacnum && (
              <>
                <div className="flex items-center gap-2 rounded bg-[#9796F314] p-4 ring-1 ring-purple-300">
                  <InformationCircle color={'rgba(151, 150, 243, 1)'} />
                  <p className="font-bold text-content-base-default">
                    This address is not associated with an existing account.
                    Create a new account by completing the below fields.
                  </p>
                </div>
                <FormSection gridClassName="grid-cols-2" className="w-full">
                  <GraniteInput
                    label="Location name"
                    {...register(`location_name`)}
                    error={errors.location_name?.message}
                  />
                  <Controller
                    {...register(`parent_account`)}
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <GraniteSelect
                        {...field}
                        options={parentAccountOptions}
                        label="Parent account"
                        onChange={onChange}
                        value={value}
                        error={errors?.parent_account?.message}
                      />
                    )}
                  />
                </FormSection>
                <FormSection gridClassName="grid-cols-3" className="w-full">
                  <Controller
                    {...register(`billing_type`)}
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <GraniteSelect
                        {...field}
                        options={billinTypeOptions}
                        label="Billing type"
                        isSearchable={false}
                        onChange={(opt: OptionType | null) => {
                          onChange(opt);
                          if (opt) resetField(`invoice_style`);
                        }}
                        value={value}
                        error={errors.billing_type?.message}
                      />
                    )}
                  />
                  <Controller
                    {...register(`invoice_style`)}
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <GraniteSelect
                        {...field}
                        options={
                          currentBillingType
                            ? getInvoiceStyleOptions(currentBillingType)
                            : []
                        }
                        label="Invoice style"
                        onChange={onChange}
                        value={value || null}
                        error={errors?.invoice_style?.message}
                      />
                    )}
                  />
                  <Controller
                    {...register(`invoice_delivery_method`)}
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <GraniteSelect
                        {...field}
                        options={invoiceDeliveryOptions}
                        label="Invoice delivery method"
                        onChange={onChange}
                        value={value}
                        error={errors?.invoice_delivery_method?.message}
                      />
                    )}
                  />
                </FormSection>
              </>
            )}
            <Divider layout="horizontal" variant="subdued" />
            <FormSection gridClassName="grid-cols-2" title="Location details">
              <GraniteInput
                {...register(`business_name`)}
                label="Business name"
                error={errors.business_name?.message}
              />
              <GraniteInput
                label="Suite/unit/flr # (optional)"
                {...register('unit')}
                error={errors.unit?.message}
              />
            </FormSection>
            <Divider layout="horizontal" variant="subdued" />
            <FormSection gridClassName="grid-cols-2" title="Location contact">
              <GraniteInput
                {...register(`name`)}
                label="Name"
                error={errors.name?.message}
              />
              <PhoneNumberInput
                error={errors.phone_number?.message}
                name={`phone_number`}
                control={control}
                label="Phone number"
              />
            </FormSection>
          </div>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <div className="flex h-full w-full flex-col gap-12 p-8">
            <ServiceAddressFormSection
              location={{ ...location, id: location?.id?.toString() }}
              className="w-full"
            />

            <FormSection gridClassName="grid-cols-3" title="Invoice details">
              <GraniteInput
                {...register(`vendor_name`)}
                label="Vendor name"
                error={errors.vendor_name?.message}
              />
              <GraniteInput
                {...register(`vendor_account_number`)}
                label="Vendor account #"
                error={errors.vendor_account_number?.message}
              />
              <GraniteInput
                {...register(`mrc`)}
                label="Total MRC"
                error={errors.mrc?.message}
              />
              <GraniteInput
                {...register(`contract_start_date`)}
                label="Contract start date (optional)"
                placeholder="mm-dd-yyyy"
                error={errors.contract_start_date?.message}
              />
              <GraniteInput
                {...register(`contract_end_date`)}
                label="Contract end date (optional)"
                placeholder="mm-dd-yyyy"
                error={errors.contract_end_date?.message}
              />
            </FormSection>
            <Divider layout="horizontal" variant="subdued" />
            <FormSection
              title="Services"
              className="w-full"
              gridClassName="grid-cols-1"
            >
              {fields.map((field, index) => {
                const serviceType =
                  watch(`services.${index}.service_type`)?.value || '';

                return (
                  <div
                    key={field.id}
                    className="relative mb-6 grid auto-rows-auto grid-cols-[40px,1fr,20px] gap-4"
                  >
                    {/* Row number */}
                    <div className="col-start-1 flex items-center justify-center pt-6">
                      <span className="text-sm font-medium text-content-base-subdued">
                        {index + 1}
                      </span>
                    </div>

                    <div className="col-start-2 flex flex-col gap-4">
                      {[
                        'DID',
                        'POTS',
                        'RCF',
                        'SIP',
                        'Toll-Free',
                        'LD',
                        'Hosted PBX/EPOTS',
                      ].includes(serviceType) ? (
                        <>
                          <div className="grid grid-cols-[1fr,1fr,1fr] gap-4">
                            <Controller
                              name={`services.${index}.service_type`}
                              control={control}
                              render={({ field }) => (
                                <GraniteSelect
                                  {...field}
                                  options={INVOICE_SERVICE_TYPES.map((t) => ({
                                    label: t,
                                    value: t,
                                  }))}
                                  label="Service type"
                                  onChange={(option) => field.onChange(option)}
                                  value={field.value}
                                  error={
                                    errors.services?.[index]?.service_type
                                      ?.value?.message
                                  }
                                />
                              )}
                            />

                            <PhoneNumberInput
                              name={`services.${index}.btn`}
                              label="Service identifier"
                              error={errors.services?.[index]?.btn?.message}
                              control={control}
                            />
                            <PhoneNumberInput
                              name={`services.${index}.wtns.0`}
                              label="Working telephone number"
                              error={
                                errors.services?.[index]?.wtns?.[0]?.message
                              }
                              control={control}
                            />
                          </div>
                          {(wtnCounts[index] || 1) > 1 && (
                            <div className="grid grid-cols-[1fr,20px,1fr,20px,1fr,20px] gap-4">
                              {Array.from(
                                { length: wtnCounts[index] - 1 },
                                (_, i) => (
                                  <>
                                    <PhoneNumberInput
                                      name={`services.${index}.wtns.${i + 1}`}
                                      label={`Working telephone number`}
                                      error={
                                        errors.services?.[index]?.wtns?.[i + 1]
                                          ?.message
                                      }
                                      className="col-span-1"
                                      control={control}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setWtnCounts((prev) => ({
                                          ...prev,
                                          [index]: (prev[index] || 1) - 1,
                                        }));
                                        const existing = watch(
                                          `services.${index}.wtns`,
                                        ) || [''];
                                        setValue(`services.${index}.wtns`, [
                                          ...existing.slice(0, i + 1),
                                          ...existing.slice(i + 2),
                                        ]);
                                      }}
                                      aria-label={`Remove WTN ${i + 2}`}
                                      className="flex items-center justify-center pt-7"
                                    >
                                      <Trash
                                        width="18px"
                                        height="18px"
                                        color="#FF315E"
                                      />
                                    </button>
                                  </>
                                ),
                              )}
                            </div>
                          )}
                          {(wtnCounts[index] || 1) < 5 && (
                            <GraniteButton
                              type="button"
                              variant="secondary"
                              className="mt-2 w-fit"
                              onClick={() => {
                                setWtnCounts((prev) => ({
                                  ...prev,
                                  [index]: (prev[index] || 1) + 1,
                                }));
                                const existing = watch(
                                  `services.${index}.wtns`,
                                ) || [''];
                                setValue(`services.${index}.wtns`, [
                                  ...existing,
                                  '',
                                ]);
                              }}
                            >
                              Add additional WTN
                            </GraniteButton>
                          )}
                        </>
                      ) : (
                        <div className="grid auto-cols-fr grid-flow-col items-start gap-4">
                          <Controller
                            name={`services.${index}.service_type`}
                            control={control}
                            render={({ field }) => (
                              <GraniteSelect
                                {...field}
                                options={INVOICE_SERVICE_TYPES.map((t) => ({
                                  label: t,
                                  value: t,
                                }))}
                                label="Service type"
                                onChange={(option) => field.onChange(option)}
                                value={field.value}
                                error={
                                  errors.services?.[index]?.service_type?.value
                                    ?.message
                                }
                              />
                            )}
                          />
                          {![
                            'DID',
                            'POTS',
                            'RCF',
                            'SIP',
                            'Toll-Free',
                            'LD',
                            'Hosted PBX/EPOTS',
                          ].includes(serviceType) && (
                            <GraniteInput
                              {...register(
                                `services.${index}.service_identifier`,
                              )}
                              label="Service identifier"
                              error={
                                errors.services?.[index]?.service_identifier
                                  ?.message
                              }
                            />
                          )}
                          {serviceType === 'Mobility' && (
                            <div>{renderBandwidth(index)}</div>
                          )}
                          {['Broadband', 'DIA', 'PIP/GSE'].includes(
                            serviceType,
                          ) && (
                            <>
                              <GraniteSelect
                                {...register(`services.${index}.subtype`)}
                                options={INVOICE_SERVICE_SUBTYPES.map((s) => ({
                                  label: s,
                                  value: s,
                                }))}
                                label="Data service type"
                                onChange={(option) =>
                                  option &&
                                  setValue(
                                    `services.${index}.subtype`,
                                    option.value,
                                  )
                                }
                                error={
                                  errors.services?.[index]?.subtype?.message
                                }
                              />
                              {renderBandwidth(index)}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => removeService(index)}
                        aria-label="Remove service"
                        className="col-start-3 w-2 justify-self-center pt-7"
                      >
                        <Trash width="22px" height="22px" color="#FF315E" />
                      </button>
                    )}
                  </div>
                );
              })}

              <GraniteButton
                type="button"
                variant="secondary"
                className="mt-2 w-fit"
                onClick={addService}
                disabled={fields.length >= 5}
              >
                Add additional services <Add width="20px" height="20px" />
              </GraniteButton>
            </FormSection>
          </div>
        )}
        <div className="mt-4 flex gap-5 px-8 pb-8">
          <GraniteButton
            size="large"
            onClick={onBack}
            variant="secondary"
            type="button"
          >
            Back
          </GraniteButton>

          {step === 2 ? (
            <GraniteButton
              size="large"
              variant="primary"
              type="submit"
              disabled={isLoading}
            >
              Consolidate invoice
              {isLoading && <Loader animationClassname="!w-5 !h-5" />}
            </GraniteButton>
          ) : (
            <GraniteButton
              size="large"
              onClick={onNext}
              type="button"
              variant="primary"
            >
              Next
            </GraniteButton>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default RebillServiceDetailsModal;
